import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'react-bootstrap';
import { useAppSelector } from '../../../../../hooks/redux';
import { ModalBox, RoundedAvatar } from '../../../../../components';
import { ClassTeam } from '../ClassesTag/ClassesTag';
import Select, { MultiValue } from 'react-select';
import { customStyles } from '../../../../../components/modal/MemberModal';
import { Group } from '../../../../../types/tournament';
import { imgOptionTeamLabel } from '../../../../../utils/imgOptionTeamLabel';
import { regenerateBoxLeaguePairs } from '../../../../../utils/boxLeagueUtils';

export const GroupsModal = (props: ModalProps) => {
  const { t } = useTranslation();

  const prefix = 'creativeDetail-';

  const { currentGroup, setGroups, groups, mode } = useAppSelector((state) => state.modals.data);

  const { currentClassId, classCollection } = useAppSelector((state) => state.class);

  const currentClass = classCollection.find(({ classId }) => classId === currentClassId);

  const [multiSelections, setMultiSelections] = useState<ClassTeam[]>([]);

  const options = useMemo(() => {
    if (!currentClass?.teams) return [];

    if (mode) {
      // Unique user ids
      const usersInGroups = new Set<number>();

      groups.forEach((group: { teams: any[] }) => {
        group.teams.forEach((team) => {
          team.users?.forEach((u: any) => {
            const id = typeof u === 'number' ? u : u.id;
            if (id) usersInGroups.add(id);
          });
        });
      });

      return currentClass.teams
        .filter((team: ClassTeam) => {
          // Must be a single player team
          if (team.users.length !== 1) return false;

          const player = team.users[0] as any;
          const playerId = typeof player === 'number' ? player : player.id;

          return !usersInGroups.has(playerId);
        })
        .map((team: ClassTeam) => imgOptionTeamLabel(team));
    } else {
      const teamsInGroups = new Set(
        groups.flatMap((group: { teams: any[]}) => 
          group.teams.map((t: { id: any }) => t.id)
        )
      );

      return currentClass.teams
        .filter((team: ClassTeam) => !teamsInGroups.has(team.id))
        .map((team: ClassTeam) => imgOptionTeamLabel(team));
    }
  }, [currentClass?.teams, mode, groups]);

  // TO DO: Remove one - ClassTeam or Team because they are the same
  // TO DO: Rewise store structure. We have duplication of data
  const handleSelect = useCallback(
    (e: MultiValue<{ value: string | number; label: string }>) => {
      setMultiSelections(
        e.map(
          (option) =>
            currentClass?.teams.find((team: ClassTeam) => team.id === option.value) as ClassTeam
        )
      );
    },
    [currentClass?.teams]
  );

  const finalTeams = useMemo(() => {
    if (multiSelections.length === 0) return currentGroup.teams;

    if (mode) {
      // Takes existing pairs, returns new pairs of teams
      return regenerateBoxLeaguePairs(
        currentGroup.teams,
        multiSelections,
        currentClass?.teams || []
      );
    } else {
      const combined = [...currentGroup.teams, ...multiSelections];

      const unique = combined.filter(
        (team, index, self) => index === self.findIndex((t) => t.id === team.id)
      );
      return unique;
    }
  }, [currentGroup.teams, multiSelections, mode, currentClass?.teams]);

  const tournamentGroups = useMemo(
    () =>
      groups.map((group: Group) =>
        group.id === currentGroup.id ? { ...group, teams: finalTeams } : group
      ),
    [groups, currentGroup.id, finalTeams]
  );

  const handleSave = () => {
    setGroups('tournamentGroups', tournamentGroups);
    props.onHide && props.onHide();
  };

  return (
    <ModalBox
      {...props}
      title={`${t(prefix + 'addTeam')} ${currentGroup?.name || ''}`}
      handleSave={handleSave}
    >
      <Select
        styles={customStyles}
        isMulti
        name="groupTeams"
        options={options}
        className="basic-multi-select holder-select"
        classNamePrefix="select"
        // @ts-ignore
        onChange={handleSelect}
        formatOptionLabel={(data: any) => (
          <div className="d-flex align-items-center">
            <RoundedAvatar avatar={data.logo} alt="Flag image" name="flag" />
            {data.label}
          </div>
        )}
      />
    </ModalBox>
  );
};
